import { supabase } from './supabase'
import { SUPABASE_URL } from '../utils/constants'
import type { RiskAnalystTool, RiskAnalystNarrative } from '../apps/early-signals/classifyEarlySignalsIntent'

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface InvokeEarlySignalsChatParams {
  messages: ChatMessage[]
  useRag?: boolean
  category?: string
  subCategory?: string
  /** When set, RAG is skipped; model must answer only from this pack. */
  factsPack?: string
}

export interface EarlySignalsChatResponse {
  content: string
}

export type ClassifyIntentResponse = {
  intent: RiskAnalystTool | RiskAnalystNarrative | 'clarify' | 'fallthrough'
  supplierName: string | null
  confidence: number
  /** Omitted by an older function. The client drops a slot that is not in the taxonomy. */
  category?: string | null
  subcategory?: string | null
  dimension?: string | null
}

export async function invokeEarlySignalsChat(
  params: InvokeEarlySignalsChatParams
): Promise<EarlySignalsChatResponse> {
  const { messages, useRag = true, category, subCategory, factsPack } = params
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession()
  if (sessionError || !session?.access_token) {
    throw new Error('Not authenticated. Please sign in again.')
  }
  const url = `${SUPABASE_URL}/functions/v1/early-signals-chat`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      messages,
      useRag: factsPack ? false : useRag,
      category,
      subCategory,
      factsPack: factsPack || undefined,
    }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = data?.error ?? res.statusText ?? 'Chat request failed. Configure AI in Admin AI Settings.'
    throw new Error(message)
  }
  return data as EarlySignalsChatResponse
}

/**
 * Closed-catalog intent classification (temperature 0). Used when keyword routing misses.
 */
export async function invokeEarlySignalsIntentClassify(
  userText: string
): Promise<ClassifyIntentResponse> {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession()
  if (sessionError || !session?.access_token) {
    throw new Error('Not authenticated. Please sign in again.')
  }
  const url = `${SUPABASE_URL}/functions/v1/early-signals-chat`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ mode: 'classify', message: userText }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = data?.error ?? res.statusText ?? 'Intent classify failed.'
    throw new Error(message)
  }
  return data as ClassifyIntentResponse
}
